/**
 * Brazilian Real (BRL) currency utilities.
 *
 * pt-BR convention: period for thousand separator, comma for decimal.
 * "R$ 1.234,56" represents 1234.56.
 *
 * Three operations are common in forms:
 *   - format a number → BRL display string (`maskCurrency(1234.5)`).
 *   - parse a display string → number (`parseCurrency("R$ 1.234,50")`).
 *   - live-input mask: each keystroke is reinterpreted as cents
 *     (`maskCurrencyInput("12345")` → "R$ 123,45").
 *
 * Uses Intl.NumberFormat for locale fidelity instead of hand-rolled regex,
 * which keeps the formatting consistent with `formatCurrency` patterns used
 * elsewhere in the app.
 */

/**
 * Format a numeric value as BRL display.
 *
 * maskCurrency(1234.5)               → "R$ 1.234,50"
 * maskCurrency(1234.5, false)        → "1.234,50"
 * maskCurrency(0)                    → "R$ 0,00"
 * maskCurrency(Number.NaN)           → ""
 */
export function maskCurrency(value: number, withSymbol = true): string {
  if (!Number.isFinite(value)) return '';
  const formatter = new Intl.NumberFormat('pt-BR', {
    style: withSymbol ? 'currency' : 'decimal',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return formatter.format(value);
}

/**
 * Parse a BRL-formatted (or raw) string back to a number.
 *
 * Detects BR format by the position of the decimal separator — comma after
 * the last dot indicates BR; otherwise treats input as raw/US decimal.
 *
 * parseCurrency("R$ 1.234,56") → 1234.56
 * parseCurrency("1.234,56")    → 1234.56
 * parseCurrency("1234.56")     → 1234.56 (US fallback)
 * parseCurrency("1234")        → 1234
 * parseCurrency("")            → NaN
 * parseCurrency("abc")         → NaN
 */
export function parseCurrency(value: string): number {
  if (!value) return Number.NaN;
  let cleaned = value.replace(/R\$|\s/g, '').trim();
  if (!cleaned) return Number.NaN;

  const lastComma = cleaned.lastIndexOf(',');
  const lastDot = cleaned.lastIndexOf('.');

  if (lastComma > lastDot) {
    // BR: remove dot thousand separators, swap comma for dot decimal.
    cleaned = cleaned.replace(/\./g, '').replace(',', '.');
  }
  // else: already-US/raw format — pass through to parseFloat as-is.

  const num = parseFloat(cleaned);
  return Number.isFinite(num) ? num : Number.NaN;
}

/**
 * Live-input mask: treat the raw digit string as integer cents and render as
 * BRL. Suitable for `<input onChange={e => setValue(maskCurrencyInput(e.target.value))}/>`.
 *
 * maskCurrencyInput("")          → "R$ 0,00"
 * maskCurrencyInput("5")         → "R$ 0,05"
 * maskCurrencyInput("12345")     → "R$ 123,45"
 * maskCurrencyInput("R$ 123,45") → "R$ 1.234,50" (re-mask preserves digits)
 */
export function maskCurrencyInput(value: string, withSymbol = true): string {
  const digits = value.replace(/\D/g, '');
  const cents = digits ? parseInt(digits, 10) : 0;
  return maskCurrency(cents / 100, withSymbol);
}

/**
 * ---------------------------------------------------------------------------
 * Formatadores de EXIBIÇÃO — a unidade está no nome de propósito.
 * ---------------------------------------------------------------------------
 *
 * O sistema guarda dinheiro em DUAS unidades, às vezes no mesmo payload:
 *
 *   - `service_orders.items` (jsonb) → unitPrice/total em CENTAVOS (inteiro)
 *   - `service_orders.total_parts|labor|outsourced`, `quotes.total`,
 *     `paid_amount` → numeric(12,2), ou seja REAIS (a API converte ao derivar
 *     os totais a partir dos itens — ver sync/index.ts)
 *
 * Um formatador chamado só `formatCurrency(value: number)` aceita as duas sem
 * reclamar, e o TypeScript não tem como pegar a troca. Foi assim que a tela do
 * gestor passou a exibir uma peça de R$ 20,00 como R$ 2.000,00 — e a mandar
 * esse preço para o cliente por WhatsApp. Por isso a unidade entra no nome:
 * na hora de escrever a chamada, você é obrigado a saber o que está passando.
 *
 * Para campos de formulário use `maskCurrency`/`maskCurrencyInput` acima —
 * eles são estritos (NaN vira string vazia) porque input parcial é normal
 * enquanto se digita. Os de exibição abaixo são tolerantes: `numeric` do
 * Postgres chega como string, e coluna anulável chega como null.
 */

/**
 * Valor JÁ em reais (numeric(12,2), somas de totais). 1234.5 → "R$ 1.234,50"
 *
 * `hideCents` corta os centavos (→ "R$ 1.235"), usado em painéis onde o
 * número é uma ordem de grandeza (projeção de investimento, meta de tier) e
 * os centavos só poluem.
 */
export function formatReais(
  value: number | string | null | undefined,
  options?: { hideCents?: boolean },
): string {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  const safe = num == null || !Number.isFinite(num) ? 0 : num;
  if (!options?.hideCents) return maskCurrency(safe);
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  }).format(safe);
}

/** Valor em CENTAVOS (items[].unitPrice, items[].total). 123450 → "R$ 1.234,50" */
export function formatCentavos(value: number | string | null | undefined): string {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (num == null || !Number.isFinite(num)) return 'R$ 0,00';
  return maskCurrency(num / 100);
}

/**
 * Texto digitado → CENTAVOS inteiros, para gravar em items[].unitPrice.
 * "R$ 1.234,56" → 123456. Entrada inválida vira 0 (e não NaN, que
 * contaminaria a soma dos totais da OS).
 *
 * NÃO reusa `parseCurrency` de propósito: aquele tem fallback US, em que um
 * ponto sem vírgula depois vira separador decimal. O campo de preço da OS é
 * texto livre em pt-BR — um mecânico que digita "1.234" quer mil duzentos e
 * trinta e quatro reais, e o fallback leria R$ 1,23. Aqui o ponto é sempre
 * separador de milhar e só a vírgula é decimal.
 */
export function parseCentavos(value: string): number {
  const cleaned = value.replace(/[^\d,]/g, '').replace(',', '.');
  const num = parseFloat(cleaned);
  return Number.isFinite(num) ? Math.round(num * 100) : 0;
}
